// src/components/sensors/ViewHistory.js
// --- UPDATE: V2 — full-range history, live, stats, performance
//   • The whole selected range comes from the server (GET /sensors/:id/series, grouped in MySQL).
//     Before, the newest 500 rows were filtered in the browser — at the nodes' 15 s cadence that is
//     ~2 hours, so "Past 7 days" / "Past 30 days" silently showed only the last 2 hours.
//   • Pressure: min–max band + average line per bucket (spikes stay visible), 20 / 100 PSI limits.
//     Water level: step chart of the level, time spent in each level, level changes.
//   • Summary: readings, min / avg / max, % above / below limits, coverage (share of the range with
//     data), first / last reading.
//   • Live: readings arriving while the modal is open are appended (1 h / 6 h / 24 h ranges).
//   • Race-safe (a slow response for an old range can't overwrite the new one), cached 30 s,
//     CSV export, 6 h range, date + time axis on multi-day ranges.
//   • Falls back to GET /sensors/:id/history (newest 1,000 readings) when /series isn't deployed,
//     and says when that list is truncated.
// --- END UPDATE
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalCloseButton,
  Box, Flex, Select, Text, Spinner, useColorModeValue, Alert, AlertIcon, HStack, Icon,
  // --- UPDATE
  Button, IconButton, SimpleGrid, Tooltip, Badge,
  // --- END UPDATE
} from '@chakra-ui/react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { MdHistory, MdTimeline, MdFileDownload, MdRefresh } from 'react-icons/md';
// --- UPDATE
import { fetchSensorHistoryRaw, fetchSensorSeries, isNotFoundError } from '../../services/sensorInsightsService';
import { useSensorLiveFeed } from './useSensorLiveFeed';
import { readingTimeMs, toMillis } from './sensorLiveStatus';
// --- END UPDATE

const WATER_LEVEL_LABELS = ['EMPTY', 'LOW', 'MEDIUM', 'HIGH'];
// --- UPDATE: V2
const LEVEL_COLORS = { EMPTY: '#E53E3E', LOW: '#DD6B20', MEDIUM: '#3182CE', HIGH: '#00B5D8' };
const RANGES = [
  { value: '1h', label: 'Past 1 hour', ms: 3600000 },
  { value: '6h', label: 'Past 6 hours', ms: 21600000 },
  { value: '24h', label: 'Past 24 hours', ms: 86400000 },
  { value: '7d', label: 'Past 7 days', ms: 604800000 },
  { value: '30d', label: 'Past 30 days', ms: 2592000000 },
];
const LIVE_RANGES = new Set(['1h', '6h', '24h']);
const POINTS = 240;
const RAW_LIMIT = 1000;
const PSI_LOW = 20;
const PSI_HIGH = 100;
const LEVEL_MAP = { EMPTY: 0, LOW: 1, NORMAL: 2, MEDIUM: 2, HIGH: 3, CRITICAL: 3, FLOOD: 3 };

const isWaterRecord = (s) => ['water_level', 'well', 'float', 'level'].includes(String(s?.type || '').toLowerCase())
  || String(s?.linked_entity_type || '').toLowerCase() === 'well';

const fmtDuration = (sec) => {
  if (!Number.isFinite(sec) || sec <= 0) return '0m';
  const m = Math.round(sec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h}h ${m % 60}m` : `${Math.round(h / 24)}d`;
};
const csvEscape = (v) => { const s = v === null || v === undefined ? '' : String(v); return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };

/** Raw history rows → the same point shape the series endpoint returns. */
function rowsToPoints(rows, isWater, cutoffMs) {
  return rows
    .map((r) => {
      const t = toMillis(r.timestamp) ?? toMillis(r.received_at);
      if (t === null || t < cutoffMs) return null;
      if (isWater) {
        const lvl = String(r.level ?? r.value ?? '').toUpperCase();
        const v = LEVEL_MAP[lvl];
        return v === undefined ? null : { t, last: v, last_level: WATER_LEVEL_LABELS[v], min: v, max: v, count: 1 };
      }
      const v = parseFloat(r.psi ?? r.value ?? r.pressure);
      return Number.isFinite(v) ? { t, min: v, max: v, avg: v, last: v, count: 1 } : null;
    })
    .filter(Boolean)
    .sort((a, b) => a.t - b.t);
}

/** Summary computed in the browser for the fallback path. */
function summarize(points, isWater) {
  if (!points.length) return { count: 0 };
  if (isWater) {
    const time = { EMPTY: 0, LOW: 0, MEDIUM: 0, HIGH: 0 };
    let transitions = 0;
    points.forEach((p, i) => {
      const next = points[i + 1];
      if (next) time[p.last_level] += Math.min(600, (next.t - p.t) / 1000);
      if (i && p.last !== points[i - 1].last) transitions += 1;
    });
    return { count: points.length, transitions, time_in_level_seconds: time, first_at: new Date(points[0].t).toISOString(), last_at: new Date(points[points.length - 1].t).toISOString() };
  }
  const v = points.map((p) => p.avg);
  return {
    count: points.length,
    min: Math.min(...v), max: Math.max(...v), avg: v.reduce((a, b) => a + b, 0) / v.length,
    pct_above_high: Math.round((1000 * v.filter((x) => x > PSI_HIGH).length) / v.length) / 10,
    pct_below_low: Math.round((1000 * v.filter((x) => x < PSI_LOW).length) / v.length) / 10,
    first_at: new Date(points[0].t).toISOString(), last_at: new Date(points[points.length - 1].t).toISOString(),
  };
}
// --- END UPDATE

function ViewHistory({ isOpen, onClose, sensor }) {
  // --- UPDATE: V2 state
  const [result, setResult] = useState(null);   // { kind, points, summary, bucketSeconds, source, truncated, start, end }
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeRange, setTimeRange] = useState('24h');
  const reqRef = useRef(0);
  // --- END UPDATE

  // Top-level hooks for color modes
  const bgColor = useColorModeValue('white', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const headerBg = useColorModeValue('gray.50', 'gray.800');
  const iconBg = useColorModeValue('gray.200', 'gray.700');
  const tooltipBg = useColorModeValue('white', 'gray.800');
  // --- UPDATE
  const sub = useColorModeValue('gray.500', 'gray.400');
  const tileBg = useColorModeValue('gray.50', 'whiteAlpha.50');
  const deviceId = sensor?.device_id || sensor?.id || sensor?.deviceId;
  // the server's answer wins (it knows the sensor type); the prop is the first guess
  const sensorIsWater = isWaterRecord(sensor);
  const isWaterLevel = result?.kind ? result.kind === 'water_level' : sensorIsWater;
  // --- END UPDATE
  const chartColor = isWaterLevel ? '#0bc5ea' : '#3182ce'; // Cyan for water, Blue for pressure

  // --- UPDATE: V2 — server-side range, race-safe, with fallback
  const load = useCallback(async (force = false) => {
    if (!deviceId) return;
    const id = ++reqRef.current;
    setIsLoading(true);
    setError(null);
    const range = RANGES.find((r) => r.value === timeRange) || RANGES[2];
    try {
      let next;
      try {
        const s = await fetchSensorSeries(deviceId, { range: range.value, points: POINTS, force });
        next = {
          kind: s.kind,
          points: (s.points || []).map((p) => ({ ...p, t: Date.parse(p.t) })),
          summary: s.summary || {},
          bucketSeconds: s.bucket_seconds,
          source: 'series',
          truncated: false,
          start: Date.parse(s.start),
          end: Date.parse(s.end),
        };
      } catch (e) {
        if (!isNotFoundError(e) || /sensor not found/i.test(String(e.message))) throw e;
        // /series not deployed yet → newest RAW_LIMIT readings of the range
        const cutoff = Date.now() - range.ms;
        const raw = await fetchSensorHistoryRaw(deviceId, { start: new Date(cutoff), limit: RAW_LIMIT });
        const water = raw.kind ? raw.kind === 'water_level' : sensorIsWater;
        const points = rowsToPoints(raw.rows, water, cutoff);
        next = {
          kind: water ? 'water_level' : 'pressure', points, summary: summarize(points, water), bucketSeconds: null,
          source: 'history', truncated: raw.rows.length >= RAW_LIMIT, start: cutoff, end: Date.now(),
        };
      }
      if (id !== reqRef.current) return;
      setResult(next);
    } catch (err) {
      if (id !== reqRef.current) return;
      console.error('History Fetch Error:', err);
      setError(/sensor not found/i.test(String(err?.message)) ? 'This device has no sensor record, so no history is stored for it.' : 'Unable to retrieve historical telemetry for this node.');
    } finally {
      if (id === reqRef.current) setIsLoading(false);
    }
  }, [deviceId, timeRange, sensorIsWater]);

  useEffect(() => {
    if (isOpen && deviceId) load(false);
    if (!isOpen) reqRef.current += 1;
  }, [isOpen, deviceId, load]);
  // a different sensor must not show the previous sensor's chart
  useEffect(() => { setResult(null); setError(null); }, [deviceId]);

  // Live readings appended while open (short ranges only)
  const feed = useSensorLiveFeed(deviceId, { enabled: isOpen && LIVE_RANGES.has(timeRange) });
  const chartData = useMemo(() => {
    if (!result) return [];
    const base = result.points;
    const lastT = base.length ? base[base.length - 1].t : -Infinity;
    const live = LIVE_RANGES.has(timeRange) ? feed.recent
      .map((r) => {
        const t = toMillis(r.timestamp) ?? readingTimeMs(r);
        if (t === null || t <= lastT) return null;
        if (isWaterLevel) {
          const v = LEVEL_MAP[String(r.level ?? '').toUpperCase()];
          return v === undefined ? null : { t, last: v, last_level: WATER_LEVEL_LABELS[v], min: v, max: v, count: 1, live: true };
        }
        const v = parseFloat(r.psi ?? r.pressure ?? r.value);
        return Number.isFinite(v) ? { t, min: v, max: v, avg: v, last: v, count: 1, live: true } : null;
      })
      .filter(Boolean) : [];
    return [...base, ...live].map((p) => (isWaterLevel ? p : { ...p, band: [p.min, p.max] }));
  }, [result, feed.recent, timeRange, isWaterLevel]);
  const liveAdded = chartData.filter((p) => p.live).length;

  const multiDay = timeRange === '7d' || timeRange === '30d';
  const tickFmt = useCallback((t) => {
    const d = new Date(t);
    return multiDay ? d.toLocaleDateString([], { month: 'short', day: 'numeric' }) : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, [multiDay]);

  const exportCsv = useCallback(() => {
    if (!chartData.length) return;
    const header = isWaterLevel ? ['bucket_start', 'level', 'min_level', 'max_level', 'readings'] : ['bucket_start', 'avg_psi', 'min_psi', 'max_psi', 'last_psi', 'readings'];
    const rows = chartData.map((p) => (isWaterLevel
      ? [new Date(p.t).toISOString(), p.last_level, WATER_LEVEL_LABELS[p.min] ?? '', WATER_LEVEL_LABELS[p.max] ?? '', p.count]
      : [new Date(p.t).toISOString(), p.avg, p.min, p.max, p.last, p.count]));
    const csv = [header, ...rows].map((r) => r.map(csvEscape).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `history-${deviceId}-${timeRange}-${Date.now()}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }, [chartData, isWaterLevel, deviceId, timeRange]);
  // --- END UPDATE

  // Custom tooltips ensure we format mapped numerical data back into readable Strings
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <Box bg={tooltipBg} p={3} borderRadius="md" shadow="lg" border="1px solid" borderColor={borderColor}>
          {/* --- UPDATE: V2 — bucket details */}
          <Text fontSize="xs" color="gray.500" mb={1}>{new Date(data.t).toLocaleString()}{data.live ? ' · live' : ''}</Text>
          {isWaterLevel ? (
            <>
              <Text fontWeight="bold" color={chartColor}>Level: {data.last_level}</Text>
              {data.count > 1 && data.min !== data.max && <Text fontSize="xs">range {WATER_LEVEL_LABELS[data.min]} – {WATER_LEVEL_LABELS[data.max]}</Text>}
            </>
          ) : (
            <>
              <Text fontWeight="bold" color={chartColor}>{Number(data.avg).toFixed(2)} PSI{data.count > 1 ? ' avg' : ''}</Text>
              {data.count > 1 && <Text fontSize="xs">min {Number(data.min).toFixed(1)} · max {Number(data.max).toFixed(1)}</Text>}
            </>
          )}
          {data.count > 1 && <Text fontSize="xs" color="gray.500">{data.count} readings</Text>}
          {/* --- END UPDATE */}
        </Box>
      );
    }
    return null;
  };

  // --- UPDATE: V2 summary tiles
  const s = result?.summary || {};
  const tiles = !result ? [] : isWaterLevel
    ? [
      { label: 'Readings', value: s.count ?? 0 },
      { label: 'Level changes', value: s.transitions ?? 0 },
      { label: 'Coverage', value: s.coverage_pct !== undefined ? `${s.coverage_pct}%` : '—', hint: 'Share of the range with at least one reading' },
      { label: 'Last reading', value: s.last_at ? new Date(s.last_at).toLocaleString() : '—' },
    ]
    : [
      { label: 'Readings', value: s.count ?? 0 },
      { label: 'Min / Avg / Max', value: s.count ? `${Number(s.min).toFixed(1)} / ${Number(s.avg).toFixed(1)} / ${Number(s.max).toFixed(1)}` : '—' },
      { label: `> ${PSI_HIGH} / < ${PSI_LOW} PSI`, value: s.count ? `${s.pct_above_high ?? 0}% / ${s.pct_below_low ?? 0}%` : '—', hint: 'Share of readings outside the limits' },
      { label: 'Coverage', value: s.coverage_pct !== undefined ? `${s.coverage_pct}%` : '—', hint: 'Share of the range with at least one reading' },
    ];
  const timeIn = s.time_in_level_seconds || null;
  const timeTotal = timeIn ? Object.values(timeIn).reduce((a, b) => a + b, 0) : 0;
  // --- END UPDATE

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay backdropFilter="blur(5px)" />
      <ModalContent bg={bgColor} borderRadius="2xl" overflow="hidden">
        <ModalHeader borderBottomWidth="1px" borderColor={borderColor} bg={headerBg}>
          <Flex justify="space-between" align="center" mr={8} gap={3} flexWrap="wrap">
            <HStack spacing={3}>
              <Box p={2} bg={iconBg} borderRadius="lg">
                <Icon as={MdHistory} boxSize={5} color={chartColor} />
              </Box>
              <Box>
                <Text fontWeight="800" fontSize="lg">Telemetry Timeline</Text>
                <Text fontSize="xs" color="gray.500" fontFamily="mono">
                  {deviceId}
                </Text>
              </Box>
            </HStack>

            {/* --- UPDATE: V2 — 6 h range, refresh, CSV */}
            <HStack spacing={2}>
              {liveAdded > 0 && <Badge colorScheme="green" variant="subtle" borderRadius="full">+{liveAdded} live</Badge>}
              <Tooltip label="Refresh" hasArrow>
                <IconButton size="sm" variant="ghost" icon={<MdRefresh />} onClick={() => load(true)} isLoading={isLoading} aria-label="Refresh history" />
              </Tooltip>
              <Tooltip label="Download CSV" hasArrow>
                <IconButton size="sm" variant="ghost" icon={<MdFileDownload />} onClick={exportCsv} isDisabled={!chartData.length} aria-label="Download history CSV" />
              </Tooltip>
              <Select size="sm" w="150px" value={timeRange} onChange={(e) => setTimeRange(e.target.value)} borderRadius="lg" fontWeight="600" aria-label="Time range">
                {RANGES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </Select>
            </HStack>
            {/* --- END UPDATE */}
          </Flex>
        </ModalHeader>
        <ModalCloseButton mt={2} />

        <ModalBody py={6}>
          {/* --- UPDATE: V2 — keep the previous chart visible while a new range loads */}
          {isLoading && !result ? (
            <Flex justify="center" align="center" h="350px" direction="column">
              <Spinner color={chartColor} size="xl" thickness="4px" />
              <Text mt={4} color="gray.500" fontSize="sm" fontWeight="600">Aggregating historical records...</Text>
            </Flex>
          ) : error ? (
            <Alert status="error" borderRadius="md"><AlertIcon />{error}
              <Button size="xs" ml="auto" leftIcon={<MdRefresh />} onClick={() => load(true)}>Retry</Button>
            </Alert>
          ) : !chartData.length ? (
            <Flex justify="center" align="center" h="350px" direction="column" border="2px dashed" borderColor={borderColor} borderRadius="xl">
              <Icon as={MdTimeline} boxSize={12} color="gray.400" mb={3} />
              <Text color="gray.500" fontWeight="600">No telemetry recorded in this timeframe.</Text>
            </Flex>
          ) : (
            <Box opacity={isLoading ? 0.6 : 1} transition="opacity 0.2s">
              {result.truncated && (
                <Alert status="info" borderRadius="md" mb={3} fontSize="sm">
                  <AlertIcon />
                  Showing the newest {RAW_LIMIT.toLocaleString()} readings of this range (from {new Date(chartData[0].t).toLocaleString()}). Deploy the /series endpoint for the full range.
                </Alert>
              )}
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={2} mb={4}>
                {tiles.map((t) => (
                  <Tooltip key={t.label} label={t.hint} isDisabled={!t.hint} hasArrow>
                    <Box p={2.5} bg={tileBg} borderRadius="lg" borderWidth="1px" borderColor={borderColor}>
                      <Text fontSize="2xs" color={sub} fontWeight="700" textTransform="uppercase">{t.label}</Text>
                      <Text fontSize="sm" fontWeight="800" noOfLines={1}>{t.value}</Text>
                    </Box>
                  </Tooltip>
                ))}
              </SimpleGrid>
              {isWaterLevel && timeIn && timeTotal > 0 && (
                <Box mb={4}>
                  <Text fontSize="2xs" color={sub} fontWeight="700" textTransform="uppercase" mb={1}>Time in level</Text>
                  <Flex h="10px" borderRadius="full" overflow="hidden">
                    {WATER_LEVEL_LABELS.slice().reverse().map((l) => (timeIn[l] > 0 ? (
                      <Tooltip key={l} hasArrow label={`${l}: ${fmtDuration(timeIn[l])}`}>
                        <Box w={`${(timeIn[l] / timeTotal) * 100}%`} bg={LEVEL_COLORS[l]} />
                      </Tooltip>
                    ) : null))}
                  </Flex>
                  <HStack spacing={3} mt={1} fontSize="2xs" color={sub} flexWrap="wrap">
                    {WATER_LEVEL_LABELS.slice().reverse().map((l) => <Text key={l}><Box as="span" display="inline-block" w="8px" h="8px" borderRadius="full" bg={LEVEL_COLORS[l]} mr={1} />{l} {fmtDuration(timeIn[l])}</Text>)}
                  </HStack>
                </Box>
              )}
              <Box h="360px" w="100%">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={chartColor} stopOpacity={0.3}/>
                        <stop offset="95%" stopColor={chartColor} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={borderColor} />
                    <XAxis
                      dataKey="t" type="number" scale="time" domain={['dataMin', 'dataMax']}
                      axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#718096' }} minTickGap={30}
                      tickFormatter={tickFmt}
                    />
                    <YAxis
                      axisLine={false}
                      tickLine={false}
                      tick={{ fontSize: 10, fill: '#718096' }}
                      domain={isWaterLevel ? [0, 3] : ['auto', 'auto']}
                      ticks={isWaterLevel ? [0, 1, 2, 3] : undefined}
                      tickFormatter={(val) => (isWaterLevel ? WATER_LEVEL_LABELS[val] : val)}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />
                    {!isWaterLevel && (
                      <>
                        <ReferenceLine y={PSI_HIGH} label={{ position: 'insideTopLeft', value: 'High Warning', fill: '#e53e3e', fontSize: 10 }} stroke="#e53e3e" strokeDasharray="3 3" />
                        <ReferenceLine y={PSI_LOW} label={{ position: 'insideBottomLeft', value: 'Low Warning', fill: '#dd6b20', fontSize: 10 }} stroke="#dd6b20" strokeDasharray="3 3" />
                        <Area type="monotone" dataKey="band" stroke="none" fill={chartColor} fillOpacity={0.15} isAnimationActive={false} name="min–max" />
                        <Line type="monotone" dataKey="avg" stroke={chartColor} strokeWidth={2.5} dot={false} isAnimationActive={false} activeDot={{ r: 5, strokeWidth: 0, fill: chartColor }} name="avg" />
                      </>
                    )}
                    {isWaterLevel && (
                      <Area type="stepAfter" dataKey="last" stroke={chartColor} strokeWidth={2.5} fill="url(#colorValue)" isAnimationActive={false} activeDot={{ r: 5, strokeWidth: 0, fill: chartColor }} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </Box>
              <Text fontSize="2xs" color={sub} mt={2}>
                {result.source === 'series'
                  ? `${result.bucketSeconds >= 60 ? `${Math.round(result.bucketSeconds / 60)}-minute` : `${result.bucketSeconds}-second`} buckets${isWaterLevel ? ' (last level in each)' : ' (band = min–max, line = average)'} · ${new Date(result.start).toLocaleString()} – ${new Date(result.end).toLocaleString()}`
                  : 'Individual readings'}
              </Text>
            </Box>
          )}
          {/* --- END UPDATE */}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

export default ViewHistory;
